import { useMessageState } from './useMessageState';
import { useMessageStreaming } from './useMessageStreaming';
import { useMessageFetching } from './useMessageFetching';
import { useWebSocketConnection } from './useWebSocketConnection';
import { useMessageStatus } from './useMessageStatus';
import { useCallback } from 'react';
import { generateValidId } from '../utils/messageUtils';

export const useMessageHandling = ({ 
  chatId, 
  navigate, 
  selectedModel,
  scrollToBottom,
  onChatCreated,
  currentUser,
  onTypingEvent,
  projectId,
  clientId,
  toolCallManager
}) => {
  // Use the message state hook
  const messageState = useMessageState();
  const { 
    messages, 
    setMessages, 
    isLoading, 
    setIsLoading, 
    isLoadingChat,
    setIsLoadingChat,
    error, 
    setError, 
    typingMessage, 
    setTypingMessage, 
    messagesRef, 
    sendingMessage, 
    sseStreamingActive
  } = messageState;

  // Use message streaming hook
  const { handleSend, cancelResponse, deepResearchProgress } = useMessageStreaming({
    setMessages,
    setError,
    setIsLoading,
    sseStreamingActive,
    currentUser,
    chatId,
    onChatCreated,
    projectId,
    clientId,
    toolCallManager
  });

  // Get unified loading helpers for deep research restoration
  const { updateMessageStatus, STATUS_TYPES } = useMessageStatus();

  // Use message fetching hook
  useMessageFetching({
    chatId,
    setMessages,
    setIsLoading,
    setIsLoadingChat,
    setError,
    scrollToBottom,
    updateMessageStatus,
    STATUS_TYPES,
    sseStreamingActive
  });

  // Use WebSocket connection hook
  useWebSocketConnection({
    chatId,
    onChatCreated,
    onTypingEvent,
    setMessages,
    setError,
    setIsLoading,
    sseStreamingActive,
    currentUser
  });

  // Handle regenerate functionality
  const handleReprompt = useCallback(async (botMessage) => {
    if (!botMessage || botMessage.sender !== 'bot') {
      return;
    }
    
    // Ensure we're not already loading
    if (isLoading) {
      return;
    }
    
    // Also check if message is currently streaming
    if (botMessage.isStreaming) {
      return;
    }
    
    // Find the user message that prompted this bot response
    const messageIndex = messages.findIndex(msg => msg._id === botMessage._id);
    if (messageIndex <= 0) {
      return;
    }
    
    const userMessage = messages[messageIndex - 1];
    if (!userMessage || userMessage.sender !== 'user') {
      return;
    }
    
    // Update the bot message to store multiple responses
    setMessages(prev => {
      const newMessages = [...prev];
      const botMsgIndex = newMessages.findIndex(msg => msg._id === botMessage._id);
      
      if (botMsgIndex !== -1) {
        const currentBotMessage = newMessages[botMsgIndex];
        
        // Initialize responses array if it doesn't exist or is empty
        if (!currentBotMessage.responses || currentBotMessage.responses.length === 0) {
          currentBotMessage.responses = [{
            id: 'response-1',
            text: currentBotMessage.text,
            timestamp: currentBotMessage.timestamp,
            searchSources: currentBotMessage.searchSources,
            searchStatus: currentBotMessage.searchStatus,
            hasSearched: currentBotMessage.hasSearched,
            toolCallPositions: currentBotMessage.toolCallPositions,
            hasHubSpotFunction: currentBotMessage.hasHubSpotFunction,
            hubspotFunction: currentBotMessage.hubspotFunction,
            generatedImage: currentBotMessage.generatedImage
          }];
          currentBotMessage.activeResponseIndex = 0;
        }
        
        // Clear current response and prepare for new one
        const clearedMessage = {
          ...currentBotMessage,
          text: '', // Clear the text for new regeneration
          isStreaming: true,
          isTemporary: false, // Don't make it temporary as it's a real message
          isThinking: false,
          isSearching: false,
          searchSources: [],
          searchStatus: null,
          hasSearched: false,
          toolCallPositions: [],
          hasHubSpotFunction: false,
          hubspotFunction: null,
          generatedImage: null,
          isRegenerating: true, // This flag should persist until first chunk
          isComplete: false,
          isContentStreamingComplete: false, // Reset content streaming state
          // Preserve existing responses array for navigation
          responses: currentBotMessage.responses || [],
          activeResponseIndex: currentBotMessage.activeResponseIndex || 0
        };
        
        newMessages[botMsgIndex] = clearedMessage;
      }
      
      return newMessages;
    });
    
          // Call the streaming API directly to regenerate the response (don't add a new user message)
      try {
        if (!chatId) {
          console.log('Cannot regenerate without chatId');
          return;
        }
        
        setIsLoading(true);
        const token = localStorage.getItem('token');
        
        // Use the existing chat endpoint to regenerate the response
        const endpoint = `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/stream`;
        const body = { 
          text: userMessage.text, 
          provider: selectedModel?.provider, 
          model: selectedModel?.id,
          regenerate: true, // Flag to indicate this is a regeneration
          botMessageId: botMessage._id // Tell the backend which message to regenerate
        };
        
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(body)
        });
        
        if (!response.ok) {
          // Reset loading state on error
          setIsLoading(false);
          
          // Reset message state on error
          setMessages(prev => prev.map(msg => 
            msg._id === botMessage._id ? {
              ...msg,
              isStreaming: false,
              isRegenerating: false,
              isTemporary: false
            } : msg
          ));
          
          // If the backend doesn't support regeneration yet, show a helpful message
          if (response.status === 400 || response.status === 404) {
            setError('Regeneration feature requires backend support. Please check with your development team.');
            return;
          }
          
          throw new Error(`Failed to regenerate response: ${response.status}`);
        }
        
        // Handle regeneration streaming the same way as normal messages
        sseStreamingActive.current = true;
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              sseStreamingActive.current = false;
              setIsLoading(false);
              break;
            }
            
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';
            
            // Process each complete event
            for (const line of lines) {
              if (!line.trim()) continue;
              
              const eventMatch = line.match(/^event: (.+)$/m);
              const dataMatch = line.match(/^data: (.+)$/m);
              
              if (!dataMatch) continue;
              
              const eventType = eventMatch ? eventMatch[1] : 'message';
              let data;
              try {
                data = JSON.parse(dataMatch[1]);
              } catch (e) {
                console.warn('Error parsing SSE data:', e, dataMatch[1]);
                continue;
              }
              
                             // Handle content chunks - stream them in real-time
               if (data.content) {
                 setMessages(prev => prev.map(msg => {
                   if (msg._id === botMessage._id) {
                     // For regeneration, build up the new text from chunks
                     const currentText = msg.isRegenerating ? '' : (msg.text || '');
                     const newText = currentText + data.content;
                     
                     return {
                       ...msg,
                       text: newText,
                       isStreaming: true,
                       isRegenerating: false, // Clear regeneration flag once content starts
                       isThinking: false,
                       isSearching: false
                     };
                   }
                   return msg;
                 }));
               }
              
              // Handle completion events
              if (eventType === 'done' || eventType === 'content_complete') {
                setIsLoading(false);
                
                // Update the message with proper responses array handling
                setMessages(prev => prev.map(msg => {
                  if (msg._id === botMessage._id) {
                    // Add the new response to the responses array
                    const newResponse = {
                      id: `response-${msg.responses.length + 1}`,
                      text: msg.text,
                      timestamp: new Date(),
                      searchSources: [],
                      searchStatus: null,
                      hasSearched: false,
                      toolCallPositions: [],
                      hasHubSpotFunction: false,
                      hubspotFunction: null,
                      generatedImage: null
                    };
                    
                    const updatedResponses = [...msg.responses, newResponse];
                    
                    return {
                      ...msg,
                      isStreaming: false,
                      isRegenerating: false,
                      isTemporary: false,
                      isComplete: true,
                      responses: updatedResponses,
                      activeResponseIndex: updatedResponses.length - 1 // Switch to the new response
                    };
                  }
                  return msg;
                }));
              }
            }
          }
        } catch (streamError) {
          console.error('Error processing regeneration stream:', streamError);
          sseStreamingActive.current = false;
          setIsLoading(false);
          
          // Reset the message state on streaming error
          setMessages(prev => prev.map(msg => 
            msg._id === botMessage._id ? {
              ...msg,
              isStreaming: false,
              isRegenerating: false,
              isTemporary: false,
              text: msg.responses?.[msg.activeResponseIndex]?.text || msg.responses?.[0]?.text || msg.text
            } : msg
          ));
          
          throw streamError;
        }
      
    } catch (error) {
      setError('Failed to regenerate response');
      setIsLoading(false);
      
      // Reset the message state on error
      setMessages(prev => prev.map(msg => 
        msg._id === botMessage._id ? {
          ...msg,
          isStreaming: false,
          isTemporary: false,
          isRegenerating: false,
          isComplete: true,
          text: msg.responses?.[msg.activeResponseIndex]?.text || msg.responses?.[0]?.text || msg.text, // Restore active response text
          // Preserve responses array
          responses: msg.responses || [],
          activeResponseIndex: msg.activeResponseIndex || 0
        } : msg
      ));
    }
  }, [messages, selectedModel, setMessages, setError, setIsLoading, sseStreamingActive, chatId]);

  const handleDiscussionMessage = useCallback(async (messageInput) => {
    if (!messageInput || !messageInput.trim()) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required');
      return;
    }

    const trimmedMessage = messageInput.trim();
    const optimisticId = generateValidId();
    const timestamp = new Date().toISOString();

    const optimisticMessage = {
      _id: optimisticId,
      chatId: chatId || 'temp',
      text: trimmedMessage,
      type: 'text',
      sender: 'user',
      orgId: currentUser?.orgId || null,
      userId: currentUser?.id || null,
      timestamp,
      isTemporary: true,
      isDiscussionOnly: true
    };

    setMessages(prev => {
      const currentMessages = Array.isArray(prev) ? prev : [];
      return [...currentMessages, optimisticMessage];
    });

    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL;
      if (!apiBaseUrl) {
        throw new Error('Missing API configuration');
      }

      let targetChatId = chatId;

      // If this is a brand-new project chat, create it first
      if (!targetChatId) {
        if (!projectId) {
          throw new Error('Project context is required to start this discussion');
        }

        const createResponse = await fetch(`${apiBaseUrl}/projects/${projectId}/chats`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({})
        });

        if (!createResponse.ok) {
          throw new Error('Failed to create project chat');
        }

        const createdChat = await createResponse.json();
        targetChatId = createdChat?.chat?._id;

        if (!targetChatId) {
          throw new Error('Invalid chat response from server');
        }

        if (onChatCreated) {
          onChatCreated(targetChatId);
        } else if (projectId) {
          navigate(`/chat/${targetChatId}?project=${projectId}`);
        }
      }

      const response = await fetch(`${apiBaseUrl}/chat/${targetChatId}/discussion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: trimmedMessage })
      });

      if (!response.ok) {
        throw new Error('Failed to send discussion message');
      }

      const data = await response.json();
      const persistedMessage = {
        ...(data.message || data),
        isDiscussionOnly: true
      };

      setMessages(prev => prev.map(msg => 
        msg._id === optimisticId ? { ...persistedMessage, isTemporary: false } : msg
      ));

      setError(null);
    } catch (discussionError) {
      console.error('Discussion message error:', discussionError);
      setMessages(prev => prev.filter(msg => msg._id !== optimisticId));
      setError(discussionError.message || 'Failed to send discussion message');
    }
  }, [chatId, projectId, currentUser?.id, currentUser?.orgId, setMessages, setError, onChatCreated, navigate]);

  return {
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    isLoadingChat,
    error,
    setError,
    typingMessage,
    sseStreamingActive,
    editingMessageIndex: null, // Removed editing
    setEditingMessageIndex: () => {}, // Removed editing
    editInput: '', // Removed editing
    setEditInput: () => {}, // Removed editing
    handleSend,
    handleDiscussionMessage,
    handleEdit: () => {}, // Removed editing
    handleEditSubmit: () => {}, // Removed editing
    handleReprompt,
    handleWebSocketMessage: () => {}, // Now handled by hook
    cancelResponse,
    deepResearchProgress
  };
}; 